import { GridCellSelectionModel } from '@mui/x-data-grid-premium'
import { orderBy } from 'lodash'
import { Children, useCallback, useMemo } from 'react'
import { useGetTableChildAndBacklinkTablesQuery } from '~/common/generated/apollo-operations'
import { TableSource } from '~/components/shared/DossTable'
import { DetailedTable, DetailedTableRow, TableType } from '~/components/tables/types'
import { LoadingSpinner } from '~/components/ui/atoms/loading-spinner/LoadingSpinner'
import { TableDetailsProvider } from '../../tables/TableDetailsProvider'
import { StandaloneDossTable } from './StandaloneDossTable'
import { TableMenuProvider } from './TableMenus'

interface DossTableDetailPanelProps {
  handleMergedCellSelectionModel: (model: GridCellSelectionModel) => void
  mergedCellSelectionModel: GridCellSelectionModel
  readOnly: boolean
  row: DetailedTableRow
  table: DetailedTable
}

/**
 * Detail Panel for the DossTable
 */
export function DossTableDetailPanel({
  handleMergedCellSelectionModel,
  mergedCellSelectionModel,
  readOnly,
  row,
  table,
}: DossTableDetailPanelProps) {
  const { data: tableWithChildAndBacklinkTables, loading: tableWithChildAndBacklinkTablesLoading } =
    useGetTableChildAndBacklinkTablesQuery({
      variables: { tableId: table.id },
      skip: !table.id,
    })

  const childTables = useMemo(
    () => tableWithChildAndBacklinkTables?.table.childTables || [],
    [tableWithChildAndBacklinkTables?.table.childTables],
  )

  const lookupBacklinkTables = useMemo(
    () => tableWithChildAndBacklinkTables?.table.lookupBacklinkTables || [],
    [tableWithChildAndBacklinkTables?.table.lookupBacklinkTables],
  )

  const orderedChildTables = useMemo(() => {
    return orderBy(childTables, (childTable) => childTable.orderOnRecordDetails, 'asc')
  }, [childTables])

  const orderedLookupBacklinkTables = useMemo(() => {
    return orderBy(
      lookupBacklinkTables,
      (lookupBacklinkTable) => lookupBacklinkTable.orderOnRecordDetails,
      'asc',
    )
  }, [lookupBacklinkTables])

  // Any column from the right table should match since we are filtering on the backlink row id
  const lookupBacklinkColumnId = useCallback(
    (tableId: string): string | undefined => {
      const columns = table.columns
        .flatMap((column) => column.lookupColumnChildren)
        .filter((column) => column.lookupColumnGroup?.showBacklink)
      return columns.find((column) => column.table.id === tableId)?.id
    },
    [table.columns],
  )

  if (tableWithChildAndBacklinkTablesLoading) {
    return (
      <div className="flex items-center justify-center bg-gray-1 p-4">
        <LoadingSpinner type="table-cell" />
      </div>
    )
  }

  return (
    <div className="ms-8 flex flex-col gap-4 overflow-hidden border-l border-gray-3 bg-gray-1">
      {Children.toArray(
        orderedChildTables.map((childTable) => (
          // 5 row table height ~225px, query 10 rows for page
          <div className="flex min-h-[45px] flex-col">
            <TableMenuProvider allowInfiniteScroll={false} tableId={childTable.id}>
              <TableDetailsProvider
                tableId={childTable.id}
                tableSource={TableSource.CHILD_IN_TABLE_DETAIL}
                tableType={TableType.CHILD_TABLE}
              >
                <StandaloneDossTable
                  childTable={childTable}
                  handleMergedCellSelectionModel={handleMergedCellSelectionModel}
                  mergedCellSelectionModel={mergedCellSelectionModel}
                  readOnly={readOnly}
                  row={row}
                  showBorderBottom={true}
                  showBorderTop={false}
                />
              </TableDetailsProvider>
            </TableMenuProvider>
          </div>
        )),
      )}
      {Children.toArray(
        orderedLookupBacklinkTables.map((lookupBacklinkTable) => {
          const backlinkColumnId = lookupBacklinkColumnId(lookupBacklinkTable.id)
          if (!backlinkColumnId) {
            return null
          }

          return (
            <div className="flex min-h-[45px] flex-col">
              <TableMenuProvider allowInfiniteScroll={false} tableId={lookupBacklinkTable.id}>
                <TableDetailsProvider
                  tableId={lookupBacklinkTable.id}
                  tableSource={TableSource.CHILD_IN_TABLE_DETAIL}
                  tableType={TableType.BACKLINK_TABLE}
                >
                  <StandaloneDossTable
                    backlinkColumnId={backlinkColumnId}
                    childTable={lookupBacklinkTable}
                    handleMergedCellSelectionModel={handleMergedCellSelectionModel}
                    mergedCellSelectionModel={mergedCellSelectionModel}
                    readOnly={true}
                    row={row}
                  />
                </TableDetailsProvider>
              </TableMenuProvider>
            </div>
          )
        }),
      )}
    </div>
  )
}
